import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useCustomQuery } from '../../graphql';
import { WorkspaceAsset } from '../domain';
import { WorkspaceRoutine } from '../../routine';
import { useCurrentAccount } from '../../auth/hooks';
import { useRoles } from '../../account/hooks';
import {
  isConfirmedNonGuestMember,
  isGuestInAnyWorkspace,
} from '../../account/domain';

const CACHE_KEY = ['workspaceasset'];

export const useWorkspaceAsset = (
  // for now, we'll assume that only one workspace works with an Asset
  assetId: string,
  disabled?: boolean
) => {
  const queryClient = useQueryClient();
  const { account, isLoading: isAccountLoading } = useCurrentAccount();
  const { roles, isLoading: isRolesLoading } = useRoles();

  // Account-wide — "is the caller a Guest of ANY workspace" — because no
  // workspace id is known before a response names one. See
  // isGuestInAnyWorkspace for why this can't be read off the caller's active
  // role. The loading arm is defence in depth rather than live control flow:
  // `enabled` below withholds the request entirely until account and roles
  // both resolve, so no query is shaped from a loading value. It stays
  // trimmed rather than untrimmed so that any future caller which bypasses
  // that gate still fails closed — sending the untrimmed query to an actual
  // guest blanks the whole page and does not recover on its own, whereas a
  // wrongly-trimmed query for a non-guest only costs a fold until the
  // self-correction below fires.
  const isGuest =
    isAccountLoading || isRolesLoading
      ? true
      : isGuestInAnyWorkspace(account?.workspaceRoles, roles);

  // The one false positive isGuest above knowingly accepts: a real member
  // who also holds a Guest role in some OTHER workspace still starts
  // trimmed, because isGuest has no workspace id to scope by yet. Once the
  // trimmed response resolves it DOES name the asset's own workspace (both
  // queries select `workspace { id }`), so re-check with the workspace-
  // scoped isGuestWorkspaceRole in the effect below and drop the trim on a
  // POSITIVE result. Starts false and only ever moves to true — it never
  // reverts to trimmed once a member has been confirmed for this asset — and
  // resets when the asset changes underneath the hook.
  const [isConfirmedMemberOfAssetWorkspace, setIsConfirmedMemberOfAssetWorkspace] =
    useState(false);

  useEffect(() => {
    setIsConfirmedMemberOfAssetWorkspace(false);
  }, [assetId]);

  const effectiveIsGuest = isGuest && !isConfirmedMemberOfAssetWorkspace;

  const filter = {
    asset: { eq: assetId },
  };

  const cacheKey = [
    ...CACHE_KEY,
    assetId,
    effectiveIsGuest ? 'guest' : 'member',
  ];

  // Gate on RESOLVED account/roles, not their loading value, so the guest
  // verdict above is never the thing deciding whether a request goes out in
  // the first place. useCurrentAccount is prewarmed by app.tsx's bootstrap
  // before any route mounts, but useRoles has no always-mounted caller and is
  // genuinely uncached on a cold session — so
  // without this, isGuest's loading-fail-closed default above fired the
  // trimmed query for every ordinary member's first asset-page load, then
  // refetched the full one the moment roles resolved: a redundant payload
  // plus a visible fold popping in on the most-visited page in the product.
  const { data, isLoading } = useCustomQuery({
    query: effectiveIsGuest ? FIND_WORKSPACE_ASSET_GUEST : FIND_WORKSPACE_ASSET,
    cacheKey,
    variables: {
      filter,
      assetId,
    },
    options: {
      enabled:
        !!assetId &&
        (disabled === undefined || !disabled) &&
        !isAccountLoading &&
        !isRolesLoading,
    },
  });

  // Only a POSITIVE determination flips the trim off: account/roles resolved,
  // the asset's own workspace id resolved (from the trimmed response), and a
  // RESOLVED workspaceRoles entry for that workspace naming a real, non-Guest
  // role -- not merely the absence of a Guest verdict, which also covers "no
  // membership record for this workspace at all". Absent or still-loading
  // data leaves effectiveIsGuest exactly as isGuest left it above — this
  // never fires on an inverted, missing, or unresolved read.
  const assetWorkspaceId: string | undefined =
    data?.findWorkspaceAssets?.items?.[0]?.workspace?.id;

  useEffect(() => {
    if (!isGuest || isAccountLoading || isRolesLoading || !assetWorkspaceId) {
      return;
    }

    if (
      isConfirmedNonGuestMember(assetWorkspaceId, account?.workspaceRoles, roles)
    ) {
      setIsConfirmedMemberOfAssetWorkspace(true);
    }
  }, [
    isGuest,
    isAccountLoading,
    isRolesLoading,
    assetWorkspaceId,
    account?.workspaceRoles,
    roles,
  ]);

  const workspaceAsset = useMemo(() => {
    if (!data) return undefined;

    const wsAsset: WorkspaceAsset = data.findWorkspaceAssets.items[0];
    // Absent entirely from the guest-trimmed query (see
    // FIND_WORKSPACE_ASSET_GUEST) rather than merely empty.
    const groupRoutinesData: WorkspaceRoutine[] =
      data.routine?.findGroupRoutinesByAssetId?.routines ?? [];
    const workspaceRoutinesData: WorkspaceRoutine[] =
      data.routine?.findWorkspaceRoutinesForAsset?.routines ?? [];

    // Create asset with combined media
    const productMedia = wsAsset?.asset?.product?.media || [];
    const assetMedia = wsAsset?.asset?.media || [];
    const addonMedia =
      wsAsset?.asset?.addons?.flatMap((addon: any) => addon?.media || []) || [];

    // Combine all media sources and deduplicate by id
    const allMedia = [...productMedia, ...assetMedia, ...addonMedia];
    const mediaMap = new Map();
    allMedia.forEach((m: any) => {
      if (m && m.id && !mediaMap.has(m.id)) {
        mediaMap.set(m.id, m);
      }
    });
    const combinedMedia = Array.from(mediaMap.values());

    const assetWithCombinedMedia = {
      ...wsAsset.asset,
      media: combinedMedia,
    };

    // Merge workspace-wide routines (isWorkspaceRoutine) into direct routines so
    // they appear on shadow asset pages without being written to workspaceasset.routines.
    const existingRoutineIds = new Set(
      wsAsset.routines?.filter(Boolean).map((r) => r.id) ?? []
    );
    const mergedRoutines = [
      ...(wsAsset.routines?.filter(Boolean) ?? []),
      ...workspaceRoutinesData.filter((r) => !existingRoutineIds.has(r.id)),
    ];

    if (wsAsset?.groups?.length && groupRoutinesData?.length) {
      // Create a new workspaceAsset with merged group routines and combined media
      return {
        ...wsAsset,
        routines: mergedRoutines,
        asset: assetWithCombinedMedia,
        groups: wsAsset.groups.filter(Boolean).map((group) => {
          // Filter routines that belong to this specific group
          const groupRoutines = groupRoutinesData.filter((routine) => {
            // Check if routine.groups contains the current group.id
            return routine.groups?.some(
              (routineGroup) => routineGroup.id === group.id
            );
          });

          // Return updated group with routines
          return {
            ...group,
            routines: groupRoutines,
          };
        }),
      } as WorkspaceAsset;
    } else {
      // No groups or routines to merge, but still merge media
      return {
        ...wsAsset,
        routines: mergedRoutines,
        groups: wsAsset.groups?.filter(Boolean),
        asset: assetWithCombinedMedia,
      } as WorkspaceAsset;
    }
  }, [data]);

  const invalidateQuery = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: cacheKey, exact: false });
  }, [queryClient, assetId, effectiveIsGuest]);

  return {
    workspaceAsset,
    // react-query reports isPending:true but isFetching:false for a disabled
    // query, so its own isLoading goes false the instant `enabled` above
    // turns false — a hook that says "not loading, no data" for the
    // account/roles-resolution window makes every composition root that
    // reads this flag render its empty state there instead of a spinner,
    // trading the double-fetch flash this gate removes for a worse one:
    // unresolved is indistinguishable from failed at that point, and the
    // empty state it produces is permanent. Folding the upstream flags back
    // in keeps this hook's isLoading true across the whole window regardless
    // of how `enabled` is derived internally.
    isLoading: isLoading || isAccountLoading || isRolesLoading,
    invalidateQuery,
  };
};

// Exported for useWorkspaceAsset.spec.ts: pinning FIND_WORKSPACE_ASSET as
// byte-identical to what shipped before the guest trim, and asserting
// FIND_WORKSPACE_ASSET_GUEST never reintroduces a guest-forbidden sub-read.
export const FIND_WORKSPACE_ASSET = `
  query findWorkspaceAssets($assetId: ID!, $filter: WorkspaceAssetFilter) {
    routine {
      findGroupRoutinesByAssetId(assetId: $assetId) {
        routines {
          id
          order
          type
          config
          deactivated
          currentVersion {
            id
          }
          groups {
            id
          }
          permittedRoles {
            id
            name
          }
        }
      }
      findWorkspaceRoutinesForAsset(assetId: $assetId) {
        routines {
          id
          order
          type
          config
          deactivated
          isWorkspaceRoutine
          currentVersion {
            id
          }
          permittedRoles {
            id
            name
          }
        }
      }
    }
    findWorkspaceAssets(filter: $filter) {
      items {
        id
        asset {
          id
          name
          description
          details
          lastScannedAt
          coverImage {
            id
            name
            extension
            status
            type
            createdAt
            updatedAt
          }
          media {
            id
            name
            type
            extension
            status
            url
            createdAt
            updatedAt
            categories {
              id
              name
            }
          }
          lastAddress
          lastLocation {
            coordinates
          }
          updatedAt
          addons {
            id
            name
            media {
              id
              name
              type
              extension
              status
              url
              createdAt
              updatedAt
              categories {
                id
                name
              }
            }
          }
          product {
            id
            name
            media {
              id
              name
              type
              extension
              status
              url
              createdAt
              updatedAt
              categories {
                id
                name
              }
            }
            routines {
              id
              order
              type
              config
              deactivated
              currentVersion {
                id
              }
              permittedRoles {
                id
                name
              }
            }
            routineOrder {
              workspaceRoutineId
              order
            }
          }
          serialNumber
        }
        anonymousOptions {
          hideReportAndNote
        }
        routineState {
          workspaceRoutineId
          workspaceRoutineVersionId
          state
        }
        workspace {
          id
          type
        }
        routines {
          id
          order
          type
          config
          deactivated
          currentVersion {
            id
          }
          permittedRoles {
            id
            name
          }
        }
        groups {
          id
          name
        }
        accounts {
          id
        }
        routineOrder {
          workspaceRoutineId
          order
        }
      }
    }
  }
`;

// The guest-safe variant of FIND_WORKSPACE_ASSET. Guest holds
// workspaceasset.read but not workspaceroutine.read, group.read or
// category.read, and each @relationship sub-field resolves through its OWN
// model's permission proxy — one rejected sub-read puts an errors entry on
// the response, and useFetcher throws on ANY errors entry regardless of
// partial data, blanking the whole page. So for a guest this drops:
//   - the top-level `routine { … }` selection entirely (findGroupRoutinesByAssetId /
//     findWorkspaceRoutinesForAsset must not be called at all);
//   - `routines { … }` and `groups { … }` on the workspaceAsset itself;
//   - `routines { … }` on asset.product;
//   - `categories { … }` on every media selection (asset.media,
//     asset.addons.media, asset.product.media) — Media itself resolves fine
//     (Guest holds media.read), only its nested Category relationship does not.
// A second, unrelated class of trim sits alongside the permission-driven one
// above: lastScannedAt, lastAddress, lastLocation.coordinates and
// routineState all resolve fine under Guest's asset.read/workspaceasset.read
// (this isn't a permission proxy rejection) -- they're withheld by product
// policy instead. A Guest's page is limited to the hero image and name, not
// the equipment's last-known location or operational state, so these are
// dropped from the payload rather than merely hidden in the UI, the same way
// the permission-driven trims above are.
//
// Keep this byte-identical to FIND_WORKSPACE_ASSET everywhere it isn't one of
// the sections documented above, so the two queries only ever diverge in the
// ways this comment describes.
export const FIND_WORKSPACE_ASSET_GUEST = `
  query findWorkspaceAssets($filter: WorkspaceAssetFilter) {
    findWorkspaceAssets(filter: $filter) {
      items {
        id
        asset {
          id
          name
          description
          details
          coverImage {
            id
            name
            extension
            status
            type
            createdAt
            updatedAt
          }
          media {
            id
            name
            type
            extension
            status
            url
            createdAt
            updatedAt
          }
          updatedAt
          addons {
            id
            name
            media {
              id
              name
              type
              extension
              status
              url
              createdAt
              updatedAt
            }
          }
          product {
            id
            name
            media {
              id
              name
              type
              extension
              status
              url
              createdAt
              updatedAt
            }
            routineOrder {
              workspaceRoutineId
              order
            }
          }
          serialNumber
        }
        anonymousOptions {
          hideReportAndNote
        }
        workspace {
          id
          type
        }
        accounts {
          id
        }
        routineOrder {
          workspaceRoutineId
          order
        }
      }
    }
  }
`;
